import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Users, FileCheck2, HeartHandshake, Scale, GraduationCap, Building2 } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import Reveal from "@/components/Reveal";
import ContactForm from "@/components/ContactForm";
import SEO, { hrOrgJsonLd } from "@/components/SEO";
import TrustedReferences from "@/components/TrustedReferences";

const SERVICES = [
  { icon: <Users size={22} />, title: "Fractional HR Leadership", body: "Senior HR strategy without full-time overhead. Direct partnership with your leadership team." },
  { icon: <ShieldCheck size={22} />, title: "HR Compliance Audits", body: "State and federal audit with prioritized remediation plan across FLSA, ADA, FMLA, and healthcare-specific requirements." },
  { icon: <FileCheck2 size={22} />, title: "Handbooks & Policies", body: "Custom multi-state handbooks, policy libraries, and manager guides that hold up to legal scrutiny." },
  { icon: <Scale size={22} />, title: "Employee Relations & Investigations", body: "Neutral, defensible investigations with clear findings, documentation, and recommendations." },
  { icon: <HeartHandshake size={22} />, title: "Culture & Retention", body: "Diagnostics, engagement surveys, exit interview systems, and manager coaching programs." },
  { icon: <GraduationCap size={22} />, title: "Leadership Development", body: "Manager training, executive coaching, and succession planning tailored to healthcare and mission-driven organizations." },
];

const INDUSTRIES = [
  "Behavioral Health", "Healthcare & Medical", "Nonprofit", "Professional Services",
  "Retail & Hospitality", "Manufacturing", "Tribal Organizations", "Multi-Site Growth Companies",
];

const ENGAGEMENTS = [
  { tier: "AUDIT", title: "HR Compliance Audit", desc: "One-time diagnostic across policies, files, pay, benefits, and manager practices with prioritized findings.", features: ["4–6 week engagement", "Executive readout", "Remediation roadmap", "Priority sequencing"] },
  { tier: "MONTHLY", title: "Fractional HR", desc: "Ongoing senior HR partnership with defined scope, response SLA, and quarterly strategic reviews.", featured: true, features: ["Dedicated senior consultant", "On-call employee relations", "Handbook & policy upkeep", "Quarterly strategy reviews"] },
  { tier: "PROJECT", title: "Investigations & Special Projects", desc: "Complex investigations, RIFs, comp & class studies, benefits redesigns, or ATS/HRIS selection.", features: ["Scoped SOW", "Neutral & defensible", "Documented outcomes", "Legal partner-ready"] },
];

export default function HR() {
  return (
    <SiteLayout branch="hr">
      <SEO
        title="HR Consulting for Healthcare & Growing Organizations"
        description="Fractional HR leadership, compliance audits, investigations, handbooks, and culture consulting — for behavioral health, healthcare, and mission-driven organizations."
        path="/hr"
        jsonLd={hrOrgJsonLd}
      />
      {/* HERO */}
      <section
        className="grain relative"
        style={{
          background: "var(--cream)",
          padding: "10vw 6vw 8vw",
          display: "grid",
          gridTemplateColumns: "1.2fr 0.8fr",
          gap: "6vw",
          alignItems: "center",
        }}
        data-testid="hr-hero"
      >
        <Reveal>
          <span className="eyebrow-dark">CATALYST HR CONSULTING</span>
          <h1 className="font-display mt-5 text-[clamp(42px,5vw,74px)] leading-[1.04] text-[var(--navy)]">
            HR infrastructure that <em className="italic text-[var(--hr-accent)]">protects the organization</em> and strengthens the workforce.
          </h1>
          <p className="text-[var(--muted)] text-[17px] leading-[1.75] mt-6 max-w-[640px]">
            Our HR division serves behavioral healthcare, healthcare, nonprofit, professional services,
            and other growing organizations that need experienced strategic support without immediately
            adding full-time executive overhead.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/book" className="btn btn-navy" data-testid="hr-book-btn">
              Request an HR Consultation <ArrowRight className="ml-2" size={16} />
            </Link>
            <a href="#services" className="btn btn-ghost">See services</a>
          </div>
        </Reveal>

        <Reveal className="hidden lg:block">
          <div
            className="relative"
            style={{
              minHeight: 460,
              background: "var(--navy)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              color: "white",
              padding: 40,
            }}
          >
            <div className="absolute inset-6 border border-[#50657a]" />
            <span className="tracking-[0.3em] text-xs text-[var(--gold)]">CATALYST</span>
            <strong className="font-display text-[160px] leading-none">HR</strong>
            <p className="z-10 text-neutral-300 text-sm text-center max-w-[280px]">
              People strategy grounded in behavioral science and healthcare operations.
            </p>
          </div>
        </Reveal>
      </section>

      <div className="brand-strip">
        <span>FRACTIONAL HR</span><i /><span>COMPLIANCE</span><i /><span>INVESTIGATIONS</span><i /><span>CULTURE</span><i /><span>LEADERSHIP</span>
      </div>

      {/* SERVICES */}
      <section id="services" className="section bg-white">
        <Reveal className="max-w-3xl mx-auto text-center mb-14">
          <span className="eyebrow-dark">SERVICES</span>
          <h2 className="font-display mt-5 text-[clamp(36px,4vw,52px)] leading-[1.05] text-[var(--navy)]">
            Six ways we <em className="italic text-[var(--hr-accent)]">show up</em> for your people function.
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-[1300px] mx-auto items-stretch auto-rows-fr">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={i * 70} className="h-full">
              <article className="card-editorial h-full flex flex-col" data-testid={`hr-service-${i}`}>
                <div className="text-[var(--hr-accent)] mb-4">{s.icon}</div>
                <h3 className="font-display text-2xl text-[var(--navy)] mb-3">{s.title}</h3>
                <p className="text-sm text-[var(--muted)] leading-relaxed">{s.body}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* INDUSTRIES */}
      <section id="industries" className="section bg-[var(--light)]">
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-16 max-w-[1300px] mx-auto items-start">
          <Reveal>
            <span className="eyebrow-dark">INDUSTRIES</span>
            <h2 className="font-display mt-5 text-[clamp(32px,4vw,48px)] leading-[1.05] text-[var(--navy)]">
              Sector expertise that speaks your language.
            </h2>
            <p className="text-[var(--muted)] mt-5 leading-relaxed">
              We concentrate our HR practice in complex, regulated, and multi-site environments. Our
              consultants have led people functions inside these industries — not just consulted from
              the outside.
            </p>
          </Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 items-stretch">
            {INDUSTRIES.map((i, idx) => (
              <Reveal key={i} delay={idx * 40} className="h-full">
                <div className="border border-[var(--line)] bg-white px-6 py-5 h-full flex items-center gap-3 hover:border-[var(--navy)] transition-colors" data-testid={`hr-industry-${idx}`}>
                  <Building2 size={16} className="text-[var(--hr-accent)]" />
                  <span className="font-bold text-sm text-[var(--navy)]">{i}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ENGAGEMENT PRICING */}
      <section id="pricing" className="section bg-[var(--cream)]">
        <Reveal className="max-w-3xl mx-auto text-center mb-14">
          <span className="eyebrow-dark">ENGAGEMENTS</span>
          <h2 className="font-display mt-5 text-[clamp(36px,4vw,52px)] leading-[1.05] text-[var(--navy)]">
            Choose the model that fits your stage.
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-5 max-w-[1200px] mx-auto items-stretch">
          {ENGAGEMENTS.map((e, i) => (
            <Reveal key={e.title} delay={i * 80} className="h-full">
              <article
                className={`card-editorial p-9 flex flex-col h-full ${e.featured ? "!bg-[var(--navy)] !text-white !border-[var(--navy)]" : ""}`}
                data-testid={`hr-tier-${i}`}
              >
                <div className="text-[var(--gold)] text-[10px] tracking-widest mb-4 font-bold">{e.tier}</div>
                <h3 className="font-display text-2xl mb-2">{e.title}</h3>
                <p className={`text-sm leading-relaxed mb-6 mt-4 ${e.featured ? "text-neutral-300" : "text-[var(--muted)]"}`}>{e.desc}</p>
                <ul className={`text-sm space-y-2 mb-7 flex-1 ${e.featured ? "text-neutral-300" : "text-[var(--muted)]"}`}>
                  {e.features.map((f) => <li key={f}>· {f}</li>)}
                </ul>
                <Link to="/book" className={`text-link mt-auto ${e.featured ? "!text-[var(--gold)]" : ""}`}>
                  Book a call <ArrowRight size={14} />
                </Link>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[var(--navy)] text-white grid lg:grid-cols-[0.8fr_1.2fr] gap-16 px-[6vw] py-[100px]">
        <Reveal>
          <span className="eyebrow">START A CONVERSATION</span>
          <h2 className="font-display mt-5 text-[clamp(36px,4vw,52px)] leading-[1.05]">
            Let's talk about your people function.
          </h2>
          <p className="text-neutral-300 leading-relaxed mt-5">
            Tell us where you are today, and where you'd like to be. We'll respond within one business day.
          </p>
        </Reveal>
        <Reveal>
          <ContactForm source="hr" defaultInterest="HR Consulting" />
        </Reveal>
      </section>

      <TrustedReferences
        title="HR guidance grounded in federal, state, and industry standards."
        refs={[
          { agency: "SHRM", label: "Society for Human Resource Management — practice standards", href: "https://www.shrm.org/" },
          { agency: "EEOC", label: "Equal Employment Opportunity Commission guidance", href: "https://www.eeoc.gov/" },
          { agency: "U.S. DOL", label: "Wage & Hour Division · FMLA · FLSA rulings", href: "https://www.dol.gov/agencies/whd" },
          { agency: "OSHA", label: "Workplace safety standards for healthcare", href: "https://www.osha.gov/healthcare" },
          { agency: "NLRB", label: "National Labor Relations Board decisions & policy", href: "https://www.nlrb.gov/" },
          { agency: "HRCI", label: "HR Certification Institute — governance references", href: "https://www.hrci.org/" },
        ]}
      />
    </SiteLayout>
  );
}
